// Remove Member Modal Component

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle } from 'lucide-react';
import type { Team, User } from '@/types/teams';
import { useToast } from '@/hooks/use-toast';

interface RemoveMemberModalProps {
  readonly user: User;
  readonly team: Team;
  readonly teams: Team[];
  readonly onClose: () => void;
  readonly onConfirm: () => void;
}

export function RemoveMemberModal({
  user,
  team,
  teams,
  onClose,
  onConfirm,
}: Readonly<RemoveMemberModalProps>) {
  const { toast } = useToast();
  const [isRemoving, setIsRemoving] = useState(false);
  const [reassignTeamId, setReassignTeamId] = useState<string>('team_default_standard');
  const [assignToStandard, setAssignToStandard] = useState(true);

  const isLastTeam = user.teams.length === 1;
  const standardUsersTeam = teams.find(t => t.id === 'team_default_standard');
  const otherTeams = teams.filter(t => t.id !== team.id && !t.isDefault);

  const handleRemove = async () => {
    setIsRemoving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      toast({
        title: 'Success',
        description: `${user.name} removed from ${team.name}`,
      });
      
      onConfirm();
    } catch (error) {
      console.error('Failed to remove member:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove member. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Remove {user.name} from {team.name}?</DialogTitle>
          <DialogDescription>
            This will remove the user from this team
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* User Info */}
          <div className="bg-muted/50 p-4 rounded-lg space-y-2">
            <div>
              <p className="font-medium">{user.name}</p>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>
            <div>
              <Badge variant="outline">
                Currently in {user.teams.length} {user.teams.length === 1 ? 'team' : 'teams'}
              </Badge>
            </div>
          </div>

          {/* Warning for last team */}
          {isLastTeam && (
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 p-4 rounded-lg">
              <div className="flex gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                      This is {user.name}'s only team
                    </p>
                    <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                      Users must belong to at least one team. Please select a team to assign them to.
                    </p>
                  </div>

                  {/* Team Selection */}
                  <div className="space-y-2">
                    {standardUsersTeam && (
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="assign-standard"
                          checked={assignToStandard}
                          onCheckedChange={(checked) => setAssignToStandard(checked as boolean)}
                        />
                        <label
                          htmlFor="assign-standard"
                          className="text-sm leading-none cursor-pointer"
                        >
                          Assign to <strong>{standardUsersTeam.name}</strong> team
                        </label>
                      </div>
                    )}

                    {otherTeams.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm">Or assign to another team:</p>
                        <Select
                          value={reassignTeamId}
                          onValueChange={setReassignTeamId}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {otherTeams.map(t => (
                              <SelectItem key={t.id} value={t.id}>
                                {t.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Confirmation for non-last team */}
          {!isLastTeam && (
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm">
                {user.name} will remain in {user.teams.length - 1} other {user.teams.length - 1 === 1 ? 'team' : 'teams'}.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isRemoving}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleRemove}
            disabled={isRemoving || (isLastTeam && !assignToStandard && !reassignTeamId)}
          >
            {isRemoving && 'Removing...'}
            {!isRemoving && isLastTeam && 'Remove and Reassign'}
            {!isRemoving && !isLastTeam && 'Remove from Team'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
